import type { Subscription } from '~/interfaces/subscriptions';
import { usePreferenceActions } from '~/routes/subscribe/$subscriberId/_components/v2/actions/usePreferenceActions';
import { usePublicationPreferences } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/usePublicationPreferences';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';

import { PreferencesContext, type PreferencesContextType } from './PreferencesContext';

type Props = {
  subscription: Subscription;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the subscription preferences surface on the General tab — the `PreferencesElement`
 * (publication subscriptions + custom preference fields) and its six mutation callbacks.
 * Self-calls `usePublicationPreferences` + `usePreferenceActions`. Renders no UI; the colocated
 * `PreferencesSection` child renders from context. Account / profile, subscription lifecycle,
 * and billing are separate domains and stay out.
 */
export const PreferencesProvider = ({ subscription, subscriberId, isLoggedIn, children }: Props) => {
  const { email } = subscription || {};
  const isSubscriptionInvalid = isInvalidSubscription(subscription);

  const { preferences, hasPreferences } = usePublicationPreferences(subscriberId, isLoggedIn);

  const {
    onSubscriptionPreferenceChange,
    onTrueFalsePreferenceChange,
    onShortResponsePreferenceChange,
    onDropdownPreferenceChange,
    onMultipleChoicePreferenceChange,
    onSingleChoicePreferenceChange,
  } = usePreferenceActions({ subscriberId: subscriberId || '', isLoggedIn });

  const value: PreferencesContextType = {
    showPreferences: hasPreferences && isLoggedIn && !isSubscriptionInvalid,
    preferences,
    email,
    onSubscriptionPreferenceChange,
    onTrueFalsePreferenceChange,
    onShortResponsePreferenceChange,
    onDropdownPreferenceChange,
    onMultipleChoicePreferenceChange,
    onSingleChoicePreferenceChange,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};
